'use client';

import { useMemo } from 'react';
import { CheckCircle2, Linkedin, Loader2, XCircle, type LucideIcon } from 'lucide-react';
import { Label } from '@/components/ui/forms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/forms/select';
import { InfoPopover } from '@/components/ui/feedback/InfoPopover';
import type { ContactEmailValidationMap } from '@/lib/contact-email-validation';
import { normalizeEmailForValidation } from '@/lib/contact-email-validation';
import { cn } from '@/lib/utils';

export type RecipientValidationStatus = 'pending' | 'valid' | 'invalid' | 'unknown';

/**
 * Read a recipient email's deliverability status from a validation map.
 * An empty (but present) map means validation is still in flight → `pending`.
 */
export function getRecipientValidationStatus(
  email: string,
  validations?: ContactEmailValidationMap
): RecipientValidationStatus {
  const normalized = normalizeEmailForValidation(email);
  if (!normalized) {
    return 'unknown';
  }
  if (validations && Object.keys(validations).length === 0) {
    return 'pending';
  }
  const status = validations?.[normalized]?.status;
  if (!status) {
    return 'unknown';
  }
  return status;
}

/**
 * Dedup (case-insensitive) + sort recipient options by deliverability:
 * valid → pending/unknown → invalid, preserving original order within a rank.
 * Dedup prevents colliding React keys in the Select.
 */
export function orderRecipientOptions(
  options: string[],
  validations?: ContactEmailValidationMap
): string[] {
  const statusOrder: Record<RecipientValidationStatus, number> = {
    valid: 0,
    pending: 1,
    unknown: 1,
    invalid: 2,
  };

  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const option of options) {
    if (typeof option !== 'string') continue;
    const trimmed = option.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    // Emit the normalized (lowercased) address so the selected value and the
    // value sent downstream are canonical and match the validation map keys.
    deduped.push(key);
  }

  return deduped
    .map((option, index) => ({
      option,
      index,
      rank: statusOrder[getRecipientValidationStatus(option, validations)],
    }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map(({ option }) => option);
}

export function getRecipientStatusUi(status: RecipientValidationStatus): {
  label: string;
  shortLabel: string;
  description: string;
  className: string;
  icon: LucideIcon | null;
  iconClassName: string;
} {
  if (status === 'pending') {
    return {
      label: 'Checking email now',
      shortLabel: 'Checking',
      description: 'We are currently checking email deliverability.',
      className: 'border-blue-300 bg-blue-50 text-blue-700',
      icon: Loader2,
      iconClassName: 'h-3.5 w-3.5 animate-spin',
    };
  }
  if (status === 'valid') {
    return {
      label: 'Checked: email looks deliverable',
      shortLabel: 'Deliverable',
      description: 'This address passed deliverability checks.',
      className: 'border-green-300 bg-green-50 text-green-700',
      icon: CheckCircle2,
      iconClassName: 'h-3.5 w-3.5',
    };
  }
  if (status === 'invalid') {
    return {
      label: 'Checked: email may be invalid',
      shortLabel: 'Possible issue',
      description: 'This address may bounce or reject messages.',
      className: 'border-red-300 bg-red-50 text-red-700',
      icon: XCircle,
      iconClassName: 'h-3.5 w-3.5',
    };
  }
  return {
    label: 'Email status is unknown',
    shortLabel: 'Unknown',
    description: 'Deliverability could not be confirmed.',
    className: 'border-border bg-muted/40 text-muted-foreground',
    icon: null,
    iconClassName: '',
  };
}

/**
 * A LinkedIn profile URL can appear in the recipient list alongside email
 * addresses (SwipeCard prepends the contact's `linkedInUrl`). It has no `@`,
 * so email deliverability validation always reports it `unknown` — detect it
 * so it can be pinned first and labeled distinctly instead of rendering a
 * misleading "Unknown" deliverability badge.
 */
export function isLinkedInRecipient(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.startsWith('https://linkedin') || trimmed.startsWith('https://www.linkedin');
}

const LINKEDIN_OPTION_UI: ReturnType<typeof getRecipientStatusUi> = {
  label: 'Reach out on LinkedIn',
  shortLabel: 'LinkedIn',
  description: 'This is a LinkedIn profile, not an email address.',
  className: 'border-blue-300 bg-blue-50 text-blue-700',
  icon: Linkedin,
  iconClassName: 'h-3.5 w-3.5',
};

/**
 * Status pill for one recipient option. LinkedIn URLs get a fixed "LinkedIn"
 * identity instead of running through email deliverability status (which
 * would otherwise read as an uninformative "Unknown").
 */
function getRecipientOptionUi(
  option: string,
  validations?: ContactEmailValidationMap
): ReturnType<typeof getRecipientStatusUi> {
  if (isLinkedInRecipient(option)) {
    return LINKEDIN_OPTION_UI;
  }
  return getRecipientStatusUi(getRecipientValidationStatus(option, validations));
}

export interface RecipientEmailSelectProps {
  value?: string;
  options: string[];
  validations?: ContactEmailValidationMap;
  onChange: (email: string) => void;
  /** Tighter styling for the Simple Search preview (short pill only). */
  compact?: boolean;
  disabled?: boolean;
}

export function RecipientEmailSelect({
  value,
  options,
  validations,
  onChange,
  compact = false,
  disabled = false,
}: RecipientEmailSelectProps): JSX.Element | null {
  // LinkedIn is a distinct channel, not an email address — keep it out of the
  // deliverability ordering entirely and pin it ahead of the email list. It
  // has no deliverability status to sort by, and the user wants it to read
  // first regardless of how the emails validate.
  const linkedInOptions = useMemo(() => {
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const option of options) {
      if (typeof option !== 'string') continue;
      const trimmed = option.trim();
      if (!trimmed || !isLinkedInRecipient(trimmed)) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(trimmed);
    }
    return deduped;
  }, [options]);
  const emailOptions = useMemo(
    () =>
      options.filter(
        (option): option is string => typeof option === 'string' && !isLinkedInRecipient(option.trim())
      ),
    [options]
  );
  const orderedOptions = useMemo(
    () => [...linkedInOptions, ...orderRecipientOptions(emailOptions, validations)],
    [linkedInOptions, emailOptions, validations]
  );

  const resolvedValue =
    (typeof value === 'string' ? value.trim() : '') ||
    orderedOptions.find((option) => option.trim().length > 0) ||
    '';

  const selectedStatusUi = getRecipientOptionUi(resolvedValue, validations);
  const selectedDetail = useMemo(() => {
    const normalized = normalizeEmailForValidation(resolvedValue);
    if (!normalized) {
      return null;
    }
    const detail = validations?.[normalized]?.detail;
    return typeof detail === 'string' && detail.trim().length > 0 ? detail.trim() : null;
  }, [validations, resolvedValue]);
  const selectedDescription = selectedDetail
    ? `${selectedStatusUi.description} ${selectedDetail}`
    : selectedStatusUi.description;
  const SelectedStatusIcon = selectedStatusUi.icon;

  if (orderedOptions.length === 0) {
    return null;
  }

  const selectControl = (
    <Select
      value={resolvedValue || orderedOptions[0] || ''}
      onValueChange={(next) => onChange(next)}
      disabled={disabled || orderedOptions.length <= 1}
    >
      <SelectTrigger
        aria-label="Recipient email"
        className={cn(
          'w-full min-w-0 border-0 bg-muted/30 shadow-none focus:ring-1 focus:ring-ring',
          compact ? 'h-7 px-2 text-caption font-normal' : 'h-8 px-2.5 text-label font-medium',
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="border max-h-[320px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent [scrollbar-gutter:stable]">
        {orderedOptions.map((option) => {
          const optionStatusUi = getRecipientOptionUi(option, validations);
          const OptionStatusIcon = optionStatusUi.icon;
          return (
            <SelectItem key={option} value={option} className="text-body">
              <div className="flex w-full min-w-0 items-center justify-between gap-2">
                <span className="truncate">{option}</span>
                {/* Inherit the item's text colour (dark normally, light on the
                    highlighted/selected burgundy row) and dim via opacity so the
                    status stays legible in both states. */}
                <span className="shrink-0 inline-flex items-center gap-1 text-caption opacity-70">
                  {OptionStatusIcon && (
                    <OptionStatusIcon
                      className={optionStatusUi.iconClassName.replace('animate-spin', '')}
                    />
                  )}
                  {optionStatusUi.shortLabel}
                </span>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  // With a single address there's nothing to choose — render it as a plain,
  // read-only field (no chevron / disabled-select styling, which otherwise
  // reads as a broken/blocked dropdown). The status pill still shows alongside.
  const control =
    orderedOptions.length <= 1 ? (
      <div
        className={cn(
          'flex w-full min-w-0 items-center bg-muted/30',
          compact ? 'h-7 px-2 text-caption' : 'h-8 px-2.5 text-label font-medium',
        )}
      >
        <span className="min-w-0 truncate text-foreground">{resolvedValue}</span>
      </div>
    ) : (
      selectControl
    );

  if (compact) {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <div className="min-w-0 flex-1">{control}</div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 rounded border px-1 py-0.5 text-[10px] font-medium leading-none ${selectedStatusUi.className}`}
          title={selectedDescription}
        >
          {SelectedStatusIcon && (
            <SelectedStatusIcon
              className={selectedStatusUi.iconClassName.replace('h-3.5 w-3.5', 'h-3 w-3')}
            />
          )}
          <span>{selectedStatusUi.shortLabel}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-1.5">
      <Label className="text-caption text-muted-foreground sm:w-6 shrink-0">To:</Label>
      <div className="w-full min-w-0 sm:flex-1">{control}</div>
      <div className="flex items-center gap-1.5 sm:pl-1">
        <span
          className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-caption ${selectedStatusUi.className}`}
          title={selectedDescription}
        >
          {SelectedStatusIcon && (
            <SelectedStatusIcon className={selectedStatusUi.iconClassName} />
          )}
          <span>{selectedStatusUi.label}</span>
        </span>
        <InfoPopover ariaLabel="Email status details" content={selectedDescription} />
      </div>
    </div>
  );
}
